import { inject, injectable } from 'tsyringe';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { LoggerService } from '../../logger.service';
import {
  kNameOfNotificationWindowManagerService,
  INotificationWindowManagerService,
  INotificationWindowManagerServiceDelegate,
} from '../../../interfaces/notification-window-manager-service.interface';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Global)
export class GlobalConditionCooldownFromLastNotification
  implements ICondition, INotificationWindowManagerServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'GlobalConditionCooldownFromLastNotification',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentStorageService: PersistentStorageService,
    @inject(kNameOfNotificationWindowManagerService)
    private readonly notifWindowManager: INotificationWindowManagerService,
  ) {
    this.notifWindowManager.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(): Promise<MatchResult> {
    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return [false, NoRecommendationsReasons.FailedToGetServiceConfig];
    }

    const previousNotificationCooldownExpired: boolean =
      this.isCooldownHrsFromPreviousNotificationExpired(
        configs.cooldownFromPreviousNotificationInHours,
      );

    if (!previousNotificationCooldownExpired) {
      return [false, NoRecommendationsReasons.CooldownDisplay];
    }

    return [true, undefined];
  }

  // ---------------------------------------------------------------------------
  private isCooldownHrsFromPreviousNotificationExpired(cooldownHrs: number) {
    const lastNotificationTimestamp =
      this.persistentStorageService.getLastDisplayedNotificationTimestamp();

    if (!lastNotificationTimestamp) {
      return true;
    }

    const timePassedInMs = Date.now() - lastNotificationTimestamp;
    const timePassedHrs = timePassedInMs / (1000 * 60 * 60);

    const matches = timePassedHrs >= cooldownHrs;

    const msg = `Previous notification displayed: ${Math.floor(
      timePassedHrs,
    )} hrs ago, config:${cooldownHrs} hrs, ret:${matches}`;

    this.logger.info(msg);
    return matches;
  }

  // ---------------------------------------------------------------------------
  // INotificationWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onNotificationWindowClosed() {
    this.logger.info(
      'Updating lastDisplayedNotificationTimestamp with current ' +
        `timestamp: ${Date.now()}`,
    );

    this.persistentStorageService.setLastDisplayedNotificationTimestamp(
      Date.now(),
    );
  }
}
